// components/Breadcrumbs/Breadcrumbs.jsx
//
// Route-driven breadcrumb shown under the header on every authed page. Derives
// the trail from the current path: Home → each path segment → current page.
// Known segments get nice labels and EVERY intermediate crumb is made clickable
// when a valid, accessible target exists for that level — either the segment's
// own real route, a canonical landing for a pure grouping prefix that has no
// page of its own (e.g. /admin, /crm), or a param-based parent route. Unknown/
// old pages still get a sensible humanized trail, so EVERY page has a functional
// breadcrumb with no per-page wiring. A crumb only links when its target is a
// real route — we never produce a link to a page that does not exist.

import React from "react";
import { useLocation, Link } from "react-router-dom";
import "./Breadcrumbs.scss";

// Pretty labels for path segments.
const SEGMENT_LABELS = {
  dashboard: "Home",
  admin: "Admin",
  "super-admin": "Super Admin",
  "super-admin-activity": "Super-Admin Activity",
  settings: "Settings",
  "ai-operations": "AI Operations",
  "server-access": "Server Access",
  "custom-workers": "Custom Workers",
  "admin-cost-control": "Cost Control",
  "worker-health": "Worker Health",
  "automation-pipeline": "Automation Pipeline",
  "feature-requests": "Feature Requests",
  "alert-preferences": "Alert Preferences",
  documentation: "Documentation",
  "all-companies-settings": "All Companies Settings",
  "platform-config": "Environment Variables",
  "company-settings": "Company Settings",
  "brands-settings": "Brands Settings",
  "user-permissions": "User Permissions",
  affiliations: "Affiliations",
  statuses: "Statuses",
  "support-chat": "Support Chat",
  "email-management": "Email Management",
  "seo-management": "SEO Management",
  "publish-targets": "Publish Targets",
  "chat-management": "Chat Management",
  "invoice-management": "Invoice Management",
  "newsletter-management": "Newsletter Management",
  crm: "CRM",
  customers: "Customers",
  prospects: "Prospects",
  "post-analytics": "Post Analytics",
  "seo-analytics": "SEO Analytics",
  "email-analytics": "Email Analytics",
  "activity-log": "Activity Log",
  earnings: "Earnings",
  // generic feature words (for any older/unmapped routes)
  estimates: "Estimates", issues: "Issues", revisions: "Revisions",
  permissions: "Permissions", users: "Users", docs: "Docs", projects: "Projects",
};

// Real navigable routes — an intermediate crumb links to itself if its path is here.
const KNOWN_ROUTES = new Set([
  "/dashboard", "/worker-health", "/automation-pipeline", "/admin-cost-control",
  "/custom-workers", "/admin/custom-workers", "/admin/server-access", "/admin/ai-operations",
  "/email-management", "/seo-management", "/seo-management/publish-targets", "/chat-management",
  "/invoice-management", "/newsletter-management", "/crm/customers", "/crm/prospects", "/admin/feature-requests",
  "/admin/alert-preferences", "/admin/documentation", "/admin/all-companies-settings",
  "/admin/platform-config", "/super-admin-activity", "/super-admin", "/super-admin/settings",
  "/admin/affiliations", "/admin/brands-settings", "/admin/statuses", "/admin/support-chat",
  "/activity-log", "/admin/company-settings", "/admin/user-permissions", "/post-analytics", "/seo-analytics", "/email-analytics", "/earnings",
  "/personal-area", "/all-posts", "/all-ideas",
]);

// Pure grouping prefixes that have NO page of their own (e.g. /admin, /crm).
// Their crumb links to a canonical, always-accessible landing inside the group
// so the level is still clickable and lands on a real, routable page. Targets
// were verified against App.jsx routes; each is reachable by anyone already
// viewing a page under that group.
const GROUP_FALLBACK = {
  "/admin": "/admin/company-settings",          // no /admin index → company-settings (unguarded admin page)
  "/crm": "/crm/customers",                       // CRM default tab
  "/super-admin/users": "/super-admin",           // company-user views hang off the companies list
  "/super-admin/brands": "/super-admin",
  "/super-admin/permissions": "/super-admin",
  "/personal-area/brands": "/personal-area",      // brands are listed on the personal area
};

// Param-based parent routes (real pages with an :id). An intermediate crumb whose
// accumulated path matches one of these links to itself, so e.g. drilling
// Company → User → Brand keeps every ancestor clickable back to its real page.
const KNOWN_ROUTE_PATTERNS = [
  /^\/super-admin\/users\/[^/]+$/,                 // a company's users
  /^\/super-admin\/users\/[^/]+\/[^/]+$/,          // a user's brands
  /^\/super-admin\/brands\/[^/]+$/,
  /^\/super-admin\/permissions\/[^/]+$/,
  /^\/personal-area\/brands\/[^/]+\/posts$/,
  /^\/personal-area\/brands\/[^/]+\/ideas$/,
];

// Resolve the link target for a crumb at accumulated path `acc`. The current
// (leaf) page never links. Order: own real route → grouping-prefix landing →
// param-based parent route. Returns null when no valid target exists (plain text).
function resolveTo(acc, last) {
  if (last) return null;
  if (KNOWN_ROUTES.has(acc)) return acc;
  if (GROUP_FALLBACK[acc]) return GROUP_FALLBACK[acc];
  if (KNOWN_ROUTE_PATTERNS.some((re) => re.test(acc))) return acc;
  return null;
}

// Paths where a breadcrumb would be noise (auth/landing/standalone viewers).
const HIDE_PREFIXES = ["/login", "/registration", "/reset", "/thank-you", "/stripe", "/social/connect"];

function titleCase(s) { return String(s).replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()); }
function isId(s) { return /^[0-9a-f]{12,}$/i.test(s) || /^\d+$/.test(s); }
function labelFor(seg) {
  if (SEGMENT_LABELS[seg]) return SEGMENT_LABELS[seg];
  if (isId(seg)) return "#" + String(seg).slice(0, 6);
  return titleCase(decodeURIComponent(seg));
}

// Pure: pathname → ordered crumbs [{label, to|null, current}]. Exported for tests.
export function buildTrail(pathname) {
  if (pathname === "/" || pathname === "/dashboard") return [{ label: "Home", to: null, current: true }];
  const segs = pathname.split("/").filter(Boolean);
  const crumbs = [{ label: "Home", to: "/dashboard", current: false }];
  let acc = "";
  segs.forEach((seg, i) => {
    acc += "/" + seg;
    if (acc === "/dashboard") return;
    const last = i === segs.length - 1;
    crumbs.push({ label: labelFor(seg), to: resolveTo(acc, last), current: last });
  });
  return crumbs;
}

export default function Breadcrumbs() {
  const { pathname } = useLocation();
  if (HIDE_PREFIXES.some((p) => pathname.startsWith(p))) return null;
  const crumbs = buildTrail(pathname);

  return (
    <nav className="pg-breadcrumbs" aria-label="Breadcrumb">
      {crumbs.map((c, i) => {
        const last = i === crumbs.length - 1;
        return (
          <span className="bc-item" key={i}>
            {c.to && !last
              ? <Link to={c.to} className="bc-link">{c.label}</Link>
              : <span className={last ? "bc-current" : "bc-text"}>{c.label}</span>}
            {!last && <span className="bc-sep">/</span>}
          </span>
        );
      })}
    </nav>
  );
}
